/* eslint-disable jsx-a11y/label-has-associated-control */
import { ChangeEvent, useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { format, parse } from 'date-fns';
import { currency } from 'utils/currency';
import { WizardSeats, WizardStep1FormPropTypes, WizardStep2FormPropTypes } from 'pages/buy-tickets';

import { WizardStepMovieTicketsProps } from '@app/components/movie-tickets/movie-tickets';
// import Popup from '@app/components/popup';

import CommercialLogo from '../../../public/assets/images/ipg-images/commercial.jpg';
import HNBLogo from '../../../public/assets/images/ipg-images/hnb.jpg';
import Visa from '../../../public/assets/images/ipg-images/visa-master.png';
// import Amex from '../../../public/assets/images/ipg-images/amex-union.png';

type WizardSummaryProps = {
  data: Partial<
    WizardStep1FormPropTypes & WizardStep2FormPropTypes & WizardSeats & WizardStepMovieTicketsProps
  >;
  onTermsToggle: (value: boolean) => void;
  onSubscribeToggle: (value: boolean) => void;
  newsletterSubscribed: boolean;
  onlineFee: number;
  onPaymentMethod: (value: string) => void;
  isPopupAgree: boolean;
};

const dateFormat = 'dd-MM-yyyy';
const dateFormatLengthy = 'dd-MM-yyyy - EEEE';

const SummaryStyled = styled.div`
  ${tw`mx-auto`}
  max-width: 600px;
`;

export default function WizardSummary({
  data,
  onTermsToggle,
  onSubscribeToggle,
  newsletterSubscribed = false,
  onlineFee,
  onPaymentMethod,
  isPopupAgree,
}: WizardSummaryProps): JSX.Element {
  const [selectedOption, setSelectedOption] = useState('commercial');
  const [agreed, setAgreed] = useState(isPopupAgree);
  const showtime = format(
    parse(data.scheduleObj?.date || '', dateFormat, new Date()),
    dateFormatLengthy,
  );
  const [hnbSubOption, setHnbSubOption] = useState('hnb');
  const termsOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onTermsToggle(e.target.checked);
    setAgreed(e.target.checked);
  };

  const subscribeOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onSubscribeToggle(e.target.checked);
  };

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(e.target.value);
    if (e.target.value === 'hnb' && hnbSubOption && hnbSubOption === 'paycorp') {
      onPaymentMethod('paycorp');
    } else {
      onPaymentMethod('hnb');
    }
  };

  const handleHnbSubChange = (e) => {
    setHnbSubOption(e.target.value);
    onPaymentMethod(e.target.value);
  };

  useEffect(() => {
    if (isPopupAgree) {
      onTermsToggle(true);
      setAgreed(true);
    }
  }, [isPopupAgree, onTermsToggle]);

  const seats = data.seats?.map((seat) => seat.label).join(', ');

  return (
    <SummaryStyled>
      <div className="block mb-2">
        <h4 className="text-blue-100 text-lg font-bold">Showtime</h4>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Movie Name
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {data.scheduleObj?.movie.name}
          </div>
        </div>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Cinema
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {data.scheduleObj?.cinema.label}
          </div>
        </div>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Showtime
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {showtime} - {data.scheduleObj?.showtime.time}
          </div>
        </div>
      </div>
      <div className="block mb-2">
        <h4 className="text-blue-100 text-lg font-bold">Your details</h4>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Name
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {data.customer?.name}
          </div>
        </div>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Phone Number
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {data.customer?.phoneNo}
          </div>
        </div>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Email
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {data.customer?.email}
          </div>
        </div>
      </div>
      <div className="block mb-2">
        <h4 className="text-blue-100 text-lg font-bold">Ticket details ({data.ticketsCount})</h4>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Tickets
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">
            {data.ticketsCount}
          </div>
        </div>
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Seats
          </div>
          <div className="text-white p-3 px-5 text-sm md:text-base break-all">{seats}</div>
        </div>
        {Number.parseInt(data.adults || '0', 10) && (
          <div className="row flex flex-row my-2">
            <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
              Adults
            </div>
            <div className="text-white p-3 px-5 text-sm md:text-base break-all">
              {Number.parseInt(data.adults || '0', 10)} x ({currency(data.adultTicketPrice || 0)} +{' '}
              {onlineFee || 0}) - {currency(data.adultTicketPriceTotal || 0)}
            </div>
          </div>
        )}
        {Number.parseInt(data.children || '0', 10) && (
          <div className="row flex flex-row my-2">
            <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
              Children
            </div>
            <div className="text-white p-3 px-5 text-sm md:text-base break-all">
              {Number.parseInt(data.children || '0', 10)} x ({currency(data.childTicketPrice || 0)}{' '}
              + {onlineFee || 0}) - {currency(data.childTicketPriceTotal || 0)}
            </div>
          </div>
        )}
        <div className="row flex flex-row my-2">
          <div className="text-white font-extrabold bg-blue-100 p-3 w-24 md:w-1/4 text-right">
            Total
          </div>
          <div className="text-white p-3 px-5">{currency(data.amountPaid || 0)}</div>
        </div>
      </div>
      <div className="row px-5 mt-10">
        <h3 className="text-center text-yellow-200 font-bold mb-5">
          TICKETS ONCE SOLD ARE NOT EXCHANGEABLE, TRANSFERRABLE NOR REFUNDABLE.
        </h3>
        <p className="text-center text-yellow-300">
          By clicking on ‘Card payments’ you will be redirected to the bank payment gateway website.
          Once the payment is successfully completed you will receive an SMS confirmation and email
          with your ticket(s) attached.
        </p>
      </div>
      <div className="row flex flex-row mt-10">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="flex flex-row items-start">
          <input
            className="mt-2 form-checkbox text-green-500 text-input rounded-sm"
            type="checkbox"
            checked={newsletterSubscribed}
            onChange={subscribeOnChange}
          />
          <div className="text-white pl-3 inline-block option-text">
            Yes! I would like to receive email updates on new promotions, announcements and more.
          </div>
        </label>
      </div>
      <div className="row flex flex-row mt-4 bt-10">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="flex flex-row items-center">
          <input
            type="checkbox"
            checked={agreed}
            onChange={termsOnChange}
            className="form-checkbox text-green-500 text-input rounded-sm"
          />
          <span className="text-white pl-3 option-text ">
            {' '}
            I have read and agree to the website{' '}
            <a className="underline" target="_blank" href="/terms-and-conditions">
              terms and conditions
            </a>
          </span>
        </label>
      </div>
      <div className="space-y-6 bg-black p-6 rounded-lg">
        <div className="grid md:grid-cols-2 grid-cols-none gap-6 relative">
          <div className="w-max">
            <label htmlFor="commercial" className="cursor-pointer">
              <div
                className={`px-2 rounded border-2 ${
                  selectedOption === 'commercial' ? 'border-custom-100' : 'border-custom-300'
                }`}>
                <div className="flex items-center">
                  <div className="relative h-3 w-3">
                    <input
                      type="radio"
                      id="commercial"
                      name="bank"
                      value="commercial"
                      checked={selectedOption === 'commercial'}
                      onChange={handleRadioChange}
                      className="block appearance-none border border-solid border-color-white rounded-full w-full h-full bg-white"
                    />
                    <span
                      className={`${
                        selectedOption === 'commercial' ? 'bg-custom-100' : 'bg-black'
                      } block rounded-full w-1.5 h-1.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
                    />
                  </div>
                  <img src={CommercialLogo.src} alt="Commercial" className="w-56 h-full" />
                </div>
              </div>
            </label>
          </div>
          <span className="md:block hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-px h-full bg-custom-200" />
          <div className="w-max">
            <label htmlFor="hnb" className="cursor-pointer">
              <div
                className={`px-2 rounded border-2 ${
                  selectedOption === 'hnb' ? 'border-custom-100' : 'border-custom-300'
                }`}>
                <div className="flex items-center">
                  <div className="relative h-3 w-3">
                    <input
                      type="radio"
                      id="hnb"
                      name="bank"
                      value="hnb"
                      checked={selectedOption === 'hnb'}
                      onChange={handleRadioChange}
                      className="block appearance-none border border-solid border-color-white rounded-full w-full h-full bg-white"
                    />
                    <span
                      className={`${
                        selectedOption === 'hnb' ? 'bg-custom-100' : 'bg-black'
                      } block rounded-full w-1.5 h-1.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
                    />
                  </div>
                  <img src={HNBLogo.src} alt="HNB" className="w-56 h-full" />
                </div>
              </div>
            </label>
            {selectedOption === 'hnb' && (
              <div className="flex md:flex-row flex-col md:gap-8 gap-4 mt-4">
                <label
                  htmlFor="hnbOption1"
                  className={`flex items-start gap-x-2 cursor-pointer border-2 border-solid rounded p-1.5 ${
                    hnbSubOption === 'hnb' ? 'border-primary-500' : 'border-black'
                  }`}>
                  <div className="relative h-3 w-3 mt-0.5">
                    <input
                      type="radio"
                      id="hnbOption1"
                      name="hnbSubOption"
                      value="hnb"
                      checked={hnbSubOption === 'hnb'}
                      onChange={handleHnbSubChange}
                      className="block appearance-none border border-solid border-color-white rounded-full w-full h-full bg-white"
                    />
                    <span
                      className={`${
                        hnbSubOption === 'hnb' ? 'bg-custom-100' : 'bg-black'
                      } block rounded-full w-1.5 h-1.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
                    />
                  </div>
                  <div className="">
                    <span className="block text-white mb-1 text-xs">Visa / master</span>
                    <img src={Visa.src} alt="Visa/Master" className="w-16 h-6 object-contain" />
                  </div>
                </label>
                {/* <label
                  htmlFor="hnbOption2"
                  className={`flex items-start gap-x-2 cursor-pointer border-2 border-solid rounded p-1.5 ${
                    hnbSubOption === 'paycorp' ? 'border-primary-500' : 'border-black'
                  }`}>
                  <div className="relative h-3 w-3 mt-0.5">
                    <input
                      type="radio"
                      id="hnbOption2"
                      name="hnbSubOption"
                      value="paycorp"
                      checked={hnbSubOption === 'paycorp'}
                      onChange={handleHnbSubChange}
                      className="block appearance-none border border-solid border-color-white rounded-full w-full h-full bg-white"
                    />
                    <span
                      className={`${
                        hnbSubOption === 'paycorp' ? 'bg-custom-100' : 'bg-black'
                      } block rounded-full w-1.5 h-1.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
                    />
                  </div>
                  <div className="">
                    <span className="block text-white mb-1 text-xs">Amex</span>
                    <img src={Amex.src} alt="Amex/UnionPay" className="w-16 h-6 object-contain" />
                  </div>
                </label> */}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <div className="max-h-24 bg-white text-center px-5 mt-10">
        <Image src={PaymentMethodLogos} width={623} height={100} layout="intrinsic" />
      </div> */}
    </SummaryStyled>
  );
}
